// Query keys the player screens share. The table that reads a key and the
// card that drops it are two files; the key itself belongs to neither.

import type { GiftDirection } from "./types";

/**
 * Every page and every search of a project's player list.
 *
 * This is the prefix the card drops after a deletion — the whole list of the
 * project goes, not the one page that happened to be open, because a removed
 * player shifts every page after theirs and the total on all of them.
 */
export function playersListQueryKey(projectId: string) {
  return ["players", projectId] as const;
}

/**
 * One page of the list, for one search term — what the table actually reads.
 *
 * `bots` and `online` are part of the key and not filters over one cached
 * answer: each of them changes the rows the server sends AND the total it
 * states, so every combination is its own result, and keeping them apart is
 * what makes ticking a box back cost nothing. They sit last so that dropping
 * the prefix still takes every page of every search whatever they are set to.
 */
/**
 * How many rows one page of the list holds.
 *
 * It lives beside the key rather than in the page that renders it, because it
 * is part of what the key MEANS: playersPageQueryKey names a project, a search
 * and a page number and says nothing about size, so two callers asking for
 * different sizes would share one entry and one of them would be handed the
 * other's row count.
 */
export const PLAYERS_PAGE_SIZE = 25;

export function playersPageQueryKey(
  projectId: string,
  q: string,
  page: number,
  bots: boolean,
  online: boolean,
) {
  return [...playersListQueryKey(projectId), q, page, bots, online] as const;
}

/** One player's record — the card's baseline. */
export function playerQueryKey(projectId: string, playerId: string) {
  return ["player", projectId, playerId] as const;
}

/**
 * The player's linked identities. It sits UNDER the record's key, so anything
 * that means the record alone has to say `exact` — and anything that means
 * "this player is gone" takes both by dropping the prefix.
 */
export function playerIdentitiesQueryKey(projectId: string, playerId: string) {
  return [...playerQueryKey(projectId, playerId), "identities"] as const;
}

/**
 * One direction of the player's gifts. Under the record's key as well, by the
 * same rule the identities above spell out — a deletion drops the prefix and
 * takes the gifts with it, while a profile PATCH says `exact` and leaves them
 * alone.
 *
 * The direction is part of the KEY, not a filter applied to one cached answer:
 * the server answers "sent" and "received" separately, and keeping them apart
 * is what makes flipping the tab back cost nothing.
 */
export function playerGiftsQueryKey(
  projectId: string,
  playerId: string,
  direction: GiftDirection,
) {
  return [...playerQueryKey(projectId, playerId), "gifts", direction] as const;
}

/** The deletion preflight the confirmation dialog reads. Same prefix rule. */
export function playerDeletionPreviewQueryKey(
  projectId: string,
  playerId: string,
) {
  return [...playerQueryKey(projectId, playerId), "deletion-preview"] as const;
}

/**
 * The player's signature-failure counter. Under the record's key like the rest,
 * so a deletion drops it with the prefix — and, unlike the record, it is read
 * once per card open and never refetched: the window is seven days wide and
 * does not move while somebody is looking at it.
 */
export function playerSignatureFailuresQueryKey(
  projectId: string,
  playerId: string,
) {
  return [...playerQueryKey(projectId, playerId), "signature-failures"] as const;
}

/**
 * The player's cloud save. Under the record's key like the rest — a deletion
 * drops the prefix and takes it, a profile PATCH says `exact` and leaves it.
 */
export function playerCloudSaveQueryKey(projectId: string, playerId: string) {
  return [...playerQueryKey(projectId, playerId), "cloud-save"] as const;
}

/**
 * Who invited this player and whom they invited. Under the record's key like
 * the rest — a deletion drops the prefix and takes it with the player it is
 * about, while a profile PATCH says `exact` and leaves it alone.
 */
export function playerReferralsQueryKey(projectId: string, playerId: string) {
  return [...playerQueryKey(projectId, playerId), "referrals"] as const;
}

/**
 * The guild the player is in. Under the record's key like the rest — a
 * deletion drops the prefix and takes it, a profile PATCH says `exact` and
 * leaves it alone.
 */
export function playerGuildQueryKey(projectId: string, playerId: string) {
  return [...playerQueryKey(projectId, playerId), "guild"] as const;
}

/**
 * The running cycles the player and their guild stand on. Same prefix rule as
 * the guild above.
 */
export function playerLeaderboardsQueryKey(projectId: string, playerId: string) {
  return [...playerQueryKey(projectId, playerId), "leaderboards"] as const;
}
